import { useEffect, useMemo, useRef, useState } from "react";
import { toast } from "sonner";
import { DndContext, PointerSensor, useSensor, useSensors, useDraggable, useDroppable } from "@dnd-kit/core";
import { useSalon } from "../context/SalonContext";
import { api, auth as apiAuth } from "../lib/api";
import {
    SLOT_START_HOUR,
    SLOT_END_HOUR,
    SLOT_HEIGHT_PX,
    SLOT_MINUTES,
    buildSlotLabels,
    dateToSlotIndex,
    minutesToSlots,
    isSameDay,
    STATUS_PALETTE,
    formatDuration,
    normalizeApptStatus,
    isCancelledStatus,
} from "../lib/appointments";
import TickProgressBar from "../components/ProgressBar";
import ConflictResolutionModal, { findNextFreeSlot } from "../components/conflict-resolution-modal";
import WaitlistDrawer from "../components/WaitlistDrawer";
import { waitlistApi } from "../lib/waitlistApi";
import { isDayOpen, nextOpenDay, ensureOpenDay, getDayHours } from "../lib/businessHours";
import { peekRouteHint, clearRouteHint } from "../lib/setupRoute";

// Altura mínima do card "AGUARDA" pra caber hora + cliente + serviço +
// Confirmar/Recusar sem cortar nada.
const ACTION_CARD_MIN_H = 100;

// Data do cabeçalho da Agenda. Versão completa (desktop) e abreviada (mobile,
// pra caber numa linha só ao lado das setas). A fonte Cinzel renderiza tudo em
// caixa alta, então as strings ficam em minúsculo aqui.
const fmtAgendaDateFull = (d) =>
    d.toLocaleDateString("pt-BR", { weekday: "long", day: "2-digit", month: "long", year: "numeric" });
const fmtAgendaDateShort = (d) => {
    const wd = d.toLocaleDateString("pt-BR", { weekday: "short" }).replace(/\.$/, "");
    const mon = d.toLocaleDateString("pt-BR", { month: "short" }).replace(/\.$/, "");
    return `${wd}, ${String(d.getDate()).padStart(2, "0")} ${mon} ${d.getFullYear()}`;
};

/**
 * Agenda — O Hub Central da operação.
 * - Timeline View (colunas = colaboradores, linhas = slots de 30min)
 * - Editor Fullscreen (página interna, NÃO modal)
 * - Cards com progress bar para tickets em andamento
 * - Event-driven: lê e escreve no central appointments[]
 */
const Agenda = () => {
    const { brand, professionals, appointments, clients, upsertAppointment, startAppointment, finishAppointment, cancelAppointment, setAppointmentLocalStatus, syncTenantData } = useSalon();
    const salonHours = brand?.salonHours;
    const [selectedDate, setSelectedDate] = useState(() => {
        const d = new Date();
        d.setHours(0, 0, 0, 0);
        return d;
    });
    // "hub" (4 cards de entrada) | "grid" (timeline por colaborador). O editor
    // fullscreen fica por cima de qualquer um dos dois e, ao fechar, volta pra
    // tela de onde foi aberto.
    // Avisos da Home/Avisos podem pular o hub (kinng.route.agenda.view =
    // "grid" | "waitlist"). O initializer só LÊ o hint (puro, seguro no
    // StrictMode); a remoção one-shot fica no effect abaixo.
    const [view, setView] = useState(() => (peekRouteHint("kinng.route.agenda.view") ? "grid" : "hub"));
    const [editorState, setEditorState] = useState(null); // null | { mode, slotPrefill?, apptId? }
    const [waitlistOpen, setWaitlistOpen] = useState(() => peekRouteHint("kinng.route.agenda.view") === "waitlist");
    useEffect(() => {
        clearRouteHint("kinng.route.agenda.view");
    }, []);
    const [waitlistSummary, setWaitlistSummary] = useState({ active: 0, waiting: 0, notified: 0 });
    const [waitlistRefresh, setWaitlistRefresh] = useState(0);

    useEffect(() => {
        let cancelled = false;
        const fetchSummary = async () => {
            try {
                const s = await waitlistApi.summary();
                if (!cancelled) setWaitlistSummary(s);
            } catch (_) { /* silencioso */ }
        };
        fetchSummary();
        const t = setInterval(fetchSummary, 30000);
        return () => { cancelled = true; clearInterval(t); };
    }, [waitlistRefresh]);

    // Appointments podem ser criados por fora do CRM (Agente IA via WhatsApp,
    // booking público) sem que o SalonContext local seja avisado — o cache só
    // era atualizado depois de ações manuais feitas aqui na própria Agenda
    // (ex.: confirmWaitlistDrop). Sincroniza com o backend ao montar, ao
    // trocar de dia selecionado, e a cada 30s enquanto a Agenda estiver aberta.
    useEffect(() => {
        let cancelled = false;
        const refresh = async () => {
            try { if (!cancelled) await syncTenantData(); } catch (_) { /* silencioso */ }
        };
        refresh();
        const t = setInterval(refresh, 30000);
        return () => { cancelled = true; clearInterval(t); };
    }, [selectedDate, syncTenantData]);

    // ─── Aprovação de booking público (card "AGUARDA" + modal) ───────────────
    // Optimista local (sem espelhar PATCH genérico) + endpoint dedicado. Mesma
    // lógica usada pelo AwaitingConfirmationModal e pelos botões do card.
    const isBackendId = (id) => typeof id === "string" && /^[0-9a-f]{8}-/.test(id);

    const handleConfirmBooking = async (appt) => {
        if (!appt) return;
        setAppointmentLocalStatus(appt.id, "agendado");
        if (apiAuth.getToken() && isBackendId(appt.id)) {
            try {
                await api.patch(`/tenant/appointments/${appt.id}/confirm`, {});
            } catch (_) { /* optimista já aplicado; sync reconcilia depois */ }
        }
        toast.success("Agendamento confirmado", {
            description: "WhatsApp de confirmação enviado ao cliente.",
        });
    };

    const handleRefuseBooking = async (appt, reason = "") => {
        if (!appt) return;
        cancelAppointment(appt.id); // local-only → status "cancelado"
        if (apiAuth.getToken() && isBackendId(appt.id)) {
            try {
                await api.patch(`/tenant/appointments/${appt.id}/refuse`, { reason });
            } catch (_) { /* idem */ }
        }
        toast.info("Agendamento recusado", {
            description: reason ? `Motivo: ${reason}` : "Cliente notificado via WhatsApp.",
        });
    };

    // Agendamentos vindos do link público ficam com status "awaiting_confirmation"
    // e aparecem tanto no card "AGUARDA" da Agenda quanto (agora) na Lista de
    // Espera unificada — é o MESMO registro, então confirmar/recusar num lugar
    // reflete no outro automaticamente (o filtro abaixo deixa de casar).
    const pendingAppointments = useMemo(
        () => appointments.filter((a) => normalizeApptStatus(a.status) === "awaiting_confirmation"),
        [appointments],
    );

    // Drop rápido: card da Lista de Espera solto sobre um slot da timeline
    // Abre um mini-modal para confirmar direto (sem abrir o editor completo).
    const [waitlistDropPending, setWaitlistDropPending] = useState(null); // { entry, proId, slotIdx, dateStr, timeStr }
    const [waitlistDropSaving, setWaitlistDropSaving] = useState(false);
    // "Definir horário" (item sem match da Lista de Espera): entra em modo de
    // escolha de slot — o próximo clique num horário livre já efetiva o encaixe.
    const [placingEntry, setPlacingEntry] = useState(null);

    const handleWaitlistDrop = (entry, proId, slotIdx) => {
        if (!entry || slotIdx == null) return;
        const d = new Date(selectedDate);
        const totalMin = slotIdx * SLOT_MINUTES;
        d.setHours(SLOT_START_HOUR + Math.floor(totalMin / 60), totalMin % 60, 0, 0);
        const dateStr = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
        const timeStr = `${String(d.getHours()).padStart(2, "0")}:${String(d.getMinutes()).padStart(2, "0")}`;
        // Avisa (sem bloquear) se o horário escolhido cai fora do expediente
        // configurado do dia — mesma config lida pela navegação e pelo booking público.
        const dh = getDayHours(salonHours, d);
        if (!isDayOpen(salonHours, d) || timeStr < dh.from || timeStr >= dh.to) {
            const ok = window.confirm(
                `⏰ ${timeStr} está fora do expediente configurado (${dh.from}–${dh.to}). Encaixar mesmo assim?`
            );
            if (!ok) return;
        }
        setWaitlistDropPending({ entry, proId, slotIdx, dateStr, timeStr, dateObj: d });
    };

    const confirmWaitlistDrop = async () => {
        if (!waitlistDropPending || waitlistDropSaving) return;
        const { entry, proId, dateStr, timeStr } = waitlistDropPending;
        setWaitlistDropSaving(true);
        try {
            await waitlistApi.schedule(entry.id, {
                date: dateStr,
                time: timeStr,
                professional_id: proId,
            });
            toast.success("Agendamento confirmado", {
                description: `${entry.client_name} · ${dateStr} às ${timeStr}`,
            });
            setWaitlistDropPending(null);
            setPlacingEntry(null);
            setWaitlistRefresh((v) => v + 1);
            // Refresca appointments do backend para exibir o novo card na timeline
            try { await syncTenantData(); } catch (_) { /* silent */ }
        } catch (e) {
            toast.error("Falha ao agendar", { description: e.message || "Tente novamente" });
        } finally {
            setWaitlistDropSaving(false);
        }
    };
    const [awaitingModal, setAwaitingModal] = useState(null); // appointment or null
    const [proFilter, setProFilter] = useState("all"); // "all" | proId
    const [conflict, setConflict] = useState(null);
    const [conflictPreview, setConflictPreview] = useState(null); // "proId-slotIdx" while dragging over an occupied slot
    const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 6 } }));
    const topScrollRef = useRef(null);
    const bodyScrollRef = useRef(null);
    const syncingRef = useRef(false); // prevent ping-pong infinite loops

    const handleTopScroll = (e) => {
        if (syncingRef.current) return;
        syncingRef.current = true;
        if (bodyScrollRef.current) bodyScrollRef.current.scrollLeft = e.currentTarget.scrollLeft;
        // release on next frame to allow paint
        requestAnimationFrame(() => { syncingRef.current = false; });
    };

    const handleBodyScroll = (e) => {
        if (syncingRef.current) return;
        syncingRef.current = true;
        if (topScrollRef.current) topScrollRef.current.scrollLeft = e.currentTarget.scrollLeft;
        requestAnimationFrame(() => { syncingRef.current = false; });
    };

    const handleDragOver = (event) => {
        const { active, over } = event;
        if (!over) { setConflictPreview(null); return; }
        const overData = over.data?.current;
        if (!overData || overData.kind !== "slot") { setConflictPreview(null); return; }
        const { proId, slotIdx } = overData;
        const appt = appointments.find((a) => a.id === active.id);
        if (!appt) { setConflictPreview(null); return; }
        const newDate = new Date(selectedDate);
        const totalMin = slotIdx * SLOT_MINUTES;
        newDate.setHours(SLOT_START_HOUR + Math.floor(totalMin / 60), totalMin % 60, 0, 0);
        const duration = appt.duration_min || 30;
        const newStartTs = newDate.getTime();
        const newEndTs = newStartTs + duration * 60000;
        // No-op same place — not a conflict
        if (newStartTs === appt.date && proId === appt.professionalId) {
            setConflictPreview(null);
            return;
        }
        const has = appointments.some((a) => {
            if (a.id === appt.id) return false;
            if (isCancelledStatus(a.status)) return false;
            if (a.professionalId !== proId) return false;
            const aEnd = a.estimated_end || a.date + (a.duration_min || 30) * 60000;
            return a.date < newEndTs && newStartTs < aEnd;
        });
        setConflictPreview(has ? `${proId}-${slotIdx}` : null);
    };

    const handleDragEndWithReset = (event) => {
        setConflictPreview(null);
        handleDragEnd(event);
    };

    const handleDragEnd = (event) => {
        const { active, over } = event;
        if (!over) return;
        const apptId = active.id;
        const overData = over.data?.current;
        if (!overData || overData.kind !== "slot") return;
        const { proId, slotIdx } = overData;
        const appt = appointments.find((a) => a.id === apptId);
        if (!appt) return;

        // Compute new start datetime from slot index
        const newDate = new Date(selectedDate);
        const totalMin = slotIdx * SLOT_MINUTES;
        newDate.setHours(SLOT_START_HOUR + Math.floor(totalMin / 60), totalMin % 60, 0, 0);
        const duration = appt.duration_min || 30;
        const newStartTs = newDate.getTime();
        const newEndTs = newStartTs + duration * 60000;

        // Same place — no-op
        if (newStartTs === appt.date && proId === appt.professionalId) return;

        // Conflict check
        const conflicts = appointments.filter((a) => {
            if (a.id === apptId) return false;
            if (isCancelledStatus(a.status)) return false;
            if (a.professionalId !== proId) return false;
            const aEnd = a.estimated_end || a.date + (a.duration_min || 30) * 60000;
            return a.date < newEndTs && newStartTs < aEnd;
        });

        if (conflicts.length > 0) {
            // Smart suggestions
            const dayStart = new Date(selectedDate); dayStart.setHours(0, 0, 0, 0);
            const dayEnd = new Date(selectedDate);
            dayEnd.setHours(SLOT_END_HOUR, 0, 0, 0);
            const dayApps = appointments.filter(
                (a) =>
                    a.id !== apptId &&
                    a.professionalId === proId &&
                    !isCancelledStatus(a.status) &&
                    a.date >= dayStart.getTime() &&
                    a.date < dayEnd.getTime()
            );
            const nextFreeMs = findNextFreeSlot(dayApps, newStartTs, duration, dayEnd.getTime());

            // Other pros available at the requested time
            const otherPros = professionals
                .filter((p) => p.id !== proId && p.active !== false)
                .map((p) => {
                    const c = appointments.filter((a) => {
                        if (a.professionalId !== p.id || isCancelledStatus(a.status)) return false;
                        const aEnd = a.estimated_end || a.date + (a.duration_min || 30) * 60000;
                        return a.date < newEndTs && newStartTs < aEnd;
                    });
                    return { pro: p, available: c.length === 0 };
                })
                .filter((x) => x.available)
                .map((x) => x.pro);

            setConflict({
                appt,
                requestedStartMs: newStartTs,
                duration,
                conflictAppt: conflicts[0],
                nextFreeMs,
                otherPros,
                targetProId: proId,
            });
            return;
        }

        const newPro = professionals.find((p) => p.id === proId);
        upsertAppointment({
            ...appt,
            date: newStartTs,
            time: `${String(newDate.getHours()).padStart(2, "0")}:${String(newDate.getMinutes()).padStart(2, "0")}`,
            estimated_end: newEndTs,
            professionalId: proId,
            professionalName: newPro?.name || appt.professionalName,
        });
        toast.success("Agendamento reposicionado", {
            description: `${appt.clientName} → ${newPro?.name || ""} · ${String(newDate.getHours()).padStart(2, "0")}:${String(newDate.getMinutes()).padStart(2, "0")}`,
        });
    };

    const handleConflictPickSlot = () => {
        if (!conflict || !conflict.nextFreeMs) return;
        const d = new Date(conflict.nextFreeMs);
        const targetPro = professionals.find((p) => p.id === conflict.targetProId);
        upsertAppointment({
            ...conflict.appt,
            date: conflict.nextFreeMs,
            estimated_end: conflict.nextFreeMs + conflict.duration * 60000,
            time: `${String(d.getHours()).padStart(2, "0")}:${String(d.getMinutes()).padStart(2, "0")}`,
            professionalId: conflict.targetProId,
            professionalName: targetPro?.name || conflict.appt.professionalName,
        });
        toast.success("Reagendado", {
            description: `${conflict.appt.clientName} → ${targetPro?.name || ""} às ${String(d.getHours()).padStart(2, "0")}:${String(d.getMinutes()).padStart(2, "0")}`,
        });
        setConflict(null);
    };

    const handleConflictPickPro = (newPro) => {
        if (!conflict) return;
        const d = new Date(conflict.requestedStartMs);
        upsertAppointment({
            ...conflict.appt,
            date: conflict.requestedStartMs,
            estimated_end: conflict.requestedStartMs + conflict.duration * 60000,
            time: `${String(d.getHours()).padStart(2, "0")}:${String(d.getMinutes()).padStart(2, "0")}`,
            professionalId: newPro.id,
            professionalName: newPro.name,
        });
        toast.success("Reatribuído", {
            description: `${conflict.appt.clientName} → ${newPro.name} às ${String(d.getHours()).padStart(2, "0")}:${String(d.getMinutes()).padStart(2, "0")}`,
        });
        setConflict(null);
    };

    const slotLabels = useMemo(() => buildSlotLabels(), []);

    const activePros = useMemo(() => {
        const list = professionals.filter((p) => p.active !== false);
        if (proFilter === "all") return list;
        return list.filter((p) => p.id === proFilter);
    }, [professionals, proFilter]);

    const dayAppts = useMemo(
        () => appointments.filter((a) => isSameDay(a.date, selectedDate) && !isCancelledStatus(a.status)),
        [appointments, selectedDate]
    );

    // Stats header
    const stats = useMemo(() => {
        const total = dayAppts.length;
        const inProgress = dayAppts.filter((a) => a.status === "em-andamento").length;
        const done = dayAppts.filter((a) => a.status === "concluido").length;
        const revenue = dayAppts
            .filter((a) => a.status === "concluido")
            .reduce((s, a) => s + (a.servicePrice || 0), 0);
        return { total, inProgress, done, revenue };
    }, [dayAppts]);

    // Navegação por dia: pula automaticamente dias sem expediente configurado
    // (ex.: domingo, se o tenant não abre) — nunca para num dia fechado.
    const goDay = (delta) => {
        const next = nextOpenDay(salonHours, selectedDate, delta);
        next.setHours(0, 0, 0, 0);
        setSelectedDate(next);
    };

    // Se a Agenda carregar num dia sem expediente (ex.: hoje é domingo e o
    // salão não abre aos domingos), avança sozinha pro próximo dia útil assim
    // que a config de horários chega do backend. Roda uma vez só.
    const hoursAdjustedRef = useRef(false);
    useEffect(() => {
        if (!salonHours || hoursAdjustedRef.current) return;
        hoursAdjustedRef.current = true;
        setSelectedDate((cur) => {
            if (isDayOpen(salonHours, cur)) return cur;
            const d = ensureOpenDay(salonHours, cur);
            d.setHours(0, 0, 0, 0);
            return d;
        });
    }, [salonHours]);

    // "Próximo livre": acha o colaborador com o horário livre mais cedo (a
    // partir de agora, até o fim do expediente) e rola a grade até a coluna dele.
    const handleNextFree = () => {
        const now = new Date();
        const target = new Date(selectedDate);
        target.setHours(now.getHours(), now.getMinutes(), 0, 0);
        const targetMs = target.getTime();
        const activePros = professionals.filter((p) => p.active !== false);
        const dayApps = appointments.filter((a) =>
            isSameDay(new Date(a.date), selectedDate) && !isCancelledStatus(a.status)
        );
        // Fim do expediente do dia selecionado (config real do tenant,
        // fallback 20:00) — não sugere horário depois do fechamento.
        const dh = getDayHours(salonHours, selectedDate);
        const [closeH, closeM] = (dh.to || `${SLOT_END_HOUR}:00`).split(":").map(Number);
        // For each pro, the earliest start time >= now where no overlap
        const proRanks = activePros
            .map((pro) => {
                const proApps = dayApps.filter((a) => a.professionalId === pro.id);
                const dayEnd = new Date(selectedDate);
                dayEnd.setHours(closeH || SLOT_END_HOUR, closeM || 0, 0, 0);
                const nextFree = findNextFreeSlot(proApps, targetMs, 30, dayEnd.getTime());
                return { pro, nextFree };
            })
            .filter((x) => x.nextFree !== null)
            .sort((a, b) => a.nextFree - b.nextFree);
        if (proRanks.length === 0) {
            toast.error("Nenhum colaborador livre hoje em diante");
            return;
        }
        const winner = proRanks[0];
        const proIdx = activePros.findIndex((p) => p.id === winner.pro.id);
        if (proIdx >= 0 && bodyScrollRef.current) {
            // Column width via CSS var fallback to 220
            const colW = parseFloat(
                getComputedStyle(bodyScrollRef.current).getPropertyValue("--agenda-col-min-w")
            ) || 220;
            const targetScrollLeft = Math.max(0, 80 + proIdx * colW - 40);
            bodyScrollRef.current.scrollTo({ left: targetScrollLeft, behavior: "smooth" });
        }
        const d = new Date(winner.nextFree);
        toast.success(`Próximo livre: ${winner.pro.name}`, {
            description: `${String(d.getHours()).padStart(2, "0")}:${String(d.getMinutes()).padStart(2, "0")} · clique no slot para abrir nova comanda`,
        });
    };

    // Card "Próximo Livre" do hub: abre a grade e só então roda a busca —
    // o scroll até a coluna precisa da timeline já montada (bodyScrollRef).
    const nextFreeOnGridRef = useRef(false);
    useEffect(() => {
        if (view !== "grid" || editorState || !nextFreeOnGridRef.current) return;
        nextFreeOnGridRef.current = false;
        handleNextFree();
    }); // sem deps: checa a flag a cada render, dispara uma vez só

    const openFromHub = (target) => {
        if (target === "next-free") {
            nextFreeOnGridRef.current = true;
            setView("grid");
        } else if (target === "waitlist") {
            // A lista de espera é um drawer sobre a grade (arrastar pro slot /
            // "Definir horário" precisam da timeline visível atrás).
            setView("grid");
            setWaitlistOpen(true);
        } else if (target === "new") {
            setEditorState({ mode: "create" });
        } else {
            setView("grid");
        }
    };

    const backToHub = () => {
        setWaitlistOpen(false);
        setPlacingEntry(null);
        setView("hub");
    };

    // === FULLSCREEN EDITOR ===
    if (editorState) {
        return (
            <AppointmentEditor
                state={editorState}
                onClose={() => setEditorState(null)}
                defaultDate={selectedDate}
            />
        );
    }

    // === HUB — tela de entrada (mesmo padrão do Catálogo & Estoque) ===
    if (view === "hub") {
        const pendingTotal = waitlistSummary.active + pendingAppointments.length;
        return (
            <div data-testid="agenda-hub" className="space-y-6">
                <header>
                    <div className="text-[10px] uppercase tracking-[0.3em] text-amber-300">Módulo</div>
                    <h2 className="salon-heading text-2xl text-white mt-1">Agenda</h2>
                    <p className="text-sm text-slate-500 mt-1">
                        Encaixe clientes, libere pendências e acompanhe a grade do dia.
                    </p>
                </header>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 auto-rows-fr max-w-2xl">
                    <NavCard
                        testId="agenda-card-next-free"
                        icon="fa-bolt"
                        title="Próximo Livre"
                        description="Encontra o colaborador disponível mais cedo e leva até o horário na grade"
                        accent="from-emerald-500/25 to-amber-500/5"
                        onClick={() => openFromHub("next-free")}
                    />
                    <NavCard
                        testId="agenda-card-waitlist"
                        icon="fa-hourglass-half"
                        title="Aguardando Liberação"
                        description={
                            pendingTotal > 0
                                ? `${pendingTotal} ${pendingTotal === 1 ? "pendente" : "pendentes"} · agendamentos a confirmar + lista de espera`
                                : "Agendamentos a confirmar + lista de espera"
                        }
                        badge={waitlistSummary.notified > 0 || pendingAppointments.length > 0 ? "amber" : null}
                        onClick={() => openFromHub("waitlist")}
                    />
                    <NavCard
                        testId="agenda-card-new"
                        icon="fa-calendar-plus"
                        title="Novo Agendamento"
                        description="Cliente, serviço, colaborador e horário"
                        accent="from-sky-500/25 to-amber-500/5"
                        onClick={() => openFromHub("new")}
                    />
                    <NavCard
                        testId="agenda-card-grid"
                        icon="fa-calendar-days"
                        title="Ver Agenda Completa"
                        description={`${stats.total} ${stats.total === 1 ? "agendamento" : "agendamentos"} · ${fmtAgendaDateShort(selectedDate)}`}
                        accent="from-violet-500/25 to-amber-500/5"
                        onClick={() => openFromHub("grid")}
                    />
                </div>
            </div>
        );
    }

    return (
        <div data-testid="agenda-module" style={{ "--agenda-col-min-w": "220px" }} className="space-y-4">
            <div className="flex items-center gap-3">
                <BackButton onClick={backToHub} testId="agenda-back-to-hub" />
                <div>
                    <div className="text-[10px] uppercase tracking-[0.3em] text-amber-300">Agenda</div>
                    <h2 className="salon-heading text-2xl text-white mt-0.5">Agenda Completa</h2>
                </div>
            </div>

            {/* Header — date nav + filtro + stats + ações.
                Desktop: tudo numa linha (setas · data/stats · seletor · botões).
                Mobile (flex-col): linha 1 = ◀ data ▶ · linha 2 = seletor full-width ·
                linha 3 = barra de stats compacta · linhas seguintes = botões. */}
            <header
                data-testid="agenda-header"
                className="rounded-xl border border-amber-500/15 bg-black/40 p-3 flex flex-col gap-3 md:flex-row md:flex-wrap md:items-center"
            >
                {/* ◀ data ▶ (mobile) — no desktop as setas ficam juntas e o bloco
                    data/stats ocupa o meio (md:flex-1) */}
                <div className="flex items-center gap-2 md:gap-3 md:flex-1 md:min-w-0">
                    <button
                        data-testid="agenda-day-prev"
                        onClick={() => goDay(-1)}
                        className="order-1 shrink-0 h-11 w-11 min-h-[44px] rounded-xl border border-amber-500/25 hover:border-amber-500/60 text-amber-200 transition-all"
                        title="Dia anterior"
                    >
                        <i className="fa-solid fa-chevron-left"></i>
                    </button>

                    <div className="order-2 md:order-3 flex-1 min-w-0 text-center md:text-left">
                        <div
                            data-testid="agenda-date-label"
                            className="gold-text salon-heading leading-tight text-sm truncate md:text-lg md:leading-none md:overflow-visible md:whitespace-normal"
                        >
                            <span className="md:hidden">{fmtAgendaDateShort(selectedDate)}</span>
                            <span className="hidden md:inline">{fmtAgendaDateFull(selectedDate)}</span>
                        </div>
                        {/* stats no desktop ficam aqui, sob a data */}
                        <div className="hidden md:block text-[10px] uppercase tracking-[0.2em] text-amber-300/60 mt-1 font-mono">
                            {stats.total} agendamentos · {stats.inProgress} em andamento · {stats.done} concluídos · R$ {stats.revenue.toLocaleString("pt-BR", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                        </div>
                    </div>

                    <button
                        data-testid="agenda-day-next"
                        onClick={() => goDay(1)}
                        className="order-3 md:order-2 shrink-0 h-11 w-11 min-h-[44px] rounded-xl border border-amber-500/25 hover:border-amber-500/60 text-amber-200 transition-all"
                        title="Próximo dia"
                    >
                        <i className="fa-solid fa-chevron-right"></i>
                    </button>
                </div>

                <select
                    data-testid="agenda-pro-filter"
                    value={proFilter}
                    onChange={(e) => setProFilter(e.target.value)}
                    className="w-full md:w-auto min-h-[44px] rounded-xl bg-black/60 border border-amber-500/25 px-3 text-amber-100 text-sm outline-none focus:border-amber-400/60"
                >
                    <option value="all">Todos os colaboradores</option>
                    {professionals.filter((p) => p.active !== false).map((p) => (
                        <option key={p.id} value={p.id}>{p.name}</option>
                    ))}
                </select>

                {/* barra de stats — só mobile (no desktop aparece sob a data).
                    Sem uppercase/tracking/mono pra caber numa linha; se ainda
                    quebrar em telas mínimas, quebra entre métricas (nowrap). */}
                <div
                    data-testid="agenda-stats-mobile"
                    className="md:hidden w-full flex flex-wrap items-center gap-x-1.5 gap-y-0.5 text-[10px] text-amber-300/70"
                >
                    <span className="whitespace-nowrap">{stats.total} {stats.total === 1 ? "agendamento" : "agendamentos"}</span>
                    <span className="text-amber-500/40">·</span>
                    <span className="whitespace-nowrap">{stats.inProgress} em andamento</span>
                    <span className="text-amber-500/40">·</span>
                    <span className="whitespace-nowrap">{stats.done} concluídos</span>
                    <span className="text-amber-500/40">·</span>
                    <span className="whitespace-nowrap">R$ {stats.revenue.toLocaleString("pt-BR", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
                </div>

                <button
                    data-testid="agenda-next-free-btn"
                    onClick={handleNextFree}
                    className="w-full md:w-auto justify-center md:justify-start min-h-[44px] px-3 rounded-xl border border-emerald-500/30 hover:border-emerald-400 hover:bg-emerald-500/10 text-emerald-200 text-[11px] uppercase tracking-wider font-bold flex items-center gap-2"
                    title="Encontrar e rolar até o próximo colaborador disponível"
                >
                    <i className="fa-solid fa-bolt"></i>
                    Próximo livre
                </button>

                <button
                    data-testid="agenda-waitlist-btn"
                    onClick={() => setWaitlistOpen((v) => !v)}
                    className={`w-full md:w-auto justify-center md:justify-start min-h-[44px] px-4 rounded-xl text-[11px] font-bold uppercase tracking-[0.15em] flex items-center gap-2 border transition-all ${
                        waitlistSummary.notified > 0 || pendingAppointments.length > 0
                            ? "border-amber-400/60 bg-amber-500/15 text-amber-200"
                            : "border-white/10 bg-slate-900/40 text-slate-300 hover:border-amber-500/40 hover:text-amber-200"
                    }`}
                >
                    <i className="fa-solid fa-hourglass-half"></i>
                    Lista de espera
                    {waitlistSummary.active + pendingAppointments.length > 0 && (
                        <span data-testid="agenda-waitlist-badge"
                              className={`inline-flex items-center justify-center min-w-[20px] h-5 px-1.5 rounded-full text-[10px] font-bold ${
                                  waitlistSummary.notified > 0 || pendingAppointments.length > 0 ? "bg-amber-500 text-slate-950" : "bg-white/10 text-white"
                              }`}>
                            {waitlistSummary.active + pendingAppointments.length}
                        </span>
                    )}
                </button>

                <button
                    data-testid="agenda-new-btn"
                    onClick={() => setEditorState({ mode: "create" })}
                    className="gold-pill gold-pill-active w-full md:w-auto justify-center md:justify-start min-h-[44px] px-5 rounded-xl text-[11px] font-bold uppercase tracking-[0.15em] flex items-center gap-2"
                >
                    <i className="fa-solid fa-plus"></i>
                    Novo Agendamento
                </button>
            </header>

            {placingEntry && (
                <div
                    data-testid="agenda-placing-banner"
                    className="rounded-xl border border-amber-400/50 bg-amber-500/15 px-4 py-3 flex items-center gap-3 text-amber-100"
                >
                    <i className="fa-solid fa-hand-pointer text-amber-300"></i>
                    <div className="flex-1 text-sm">
                        Escolha um horário livre para <strong>{placingEntry.client_name}</strong> — clique num slot da agenda.
                    </div>
                    <button
                        data-testid="agenda-placing-cancel"
                        onClick={() => setPlacingEntry(null)}
                        className="min-h-[36px] px-3 rounded-lg border border-white/15 text-[11px] uppercase tracking-wider font-bold hover:bg-white/10"
                    >
                        Cancelar
                    </button>
                </div>
            )}

            {/* Top horizontal scroll mirror — mirrors the body scrollLeft so the user can scroll from above */}
            <div
                ref={topScrollRef}
                data-testid="agenda-top-scroll"
                onScroll={handleTopScroll}
                className="rounded-t-lg border border-amber-500/15 border-b-0 bg-black/30 overflow-x-auto overflow-y-hidden agenda-thin-scroll"
                style={{ height: 14 }}
            >
                {/* spacer matches body scroll-width so the scrollbar size mirrors content */}
                <div style={{ width: `calc(80px + ${activePros.length} * var(--agenda-col-min-w))`, height: 1 }} />
            </div>

            {/* Timeline grid */}
            <DndContext sensors={sensors} onDragOver={handleDragOver} onDragEnd={handleDragEndWithReset} onDragCancel={() => setConflictPreview(null)} autoScroll={{ enabled: true, threshold: { x: 0.15, y: 0.15 }, acceleration: 12 }}>
                <div
                    ref={bodyScrollRef}
                    onScroll={handleBodyScroll}
                    data-testid="agenda-timeline"
                    className="rounded-2xl rounded-tl-none rounded-tr-none border border-amber-500/15 bg-black/50 overflow-x-auto agenda-thin-scroll"
                    style={{ "--agenda-col-min-w": "220px" }}
                >
                {/* Pro column headers */}
                <div
                    className="grid border-b border-amber-500/20 bg-gradient-to-b from-amber-500/8 to-transparent sticky top-0 z-10 bg-black/90 backdrop-blur"
                    style={{ gridTemplateColumns: `80px repeat(${activePros.length}, minmax(var(--agenda-col-min-w), 1fr))`, minWidth: `calc(80px + ${activePros.length} * var(--agenda-col-min-w))` }}
                >
                    <div className="px-2 py-3 text-[10px] uppercase tracking-[0.25em] text-amber-300/60 font-bold text-center">
                        Horário
                    </div>
                    {activePros.map((p) => (
                        <div
                            key={p.id}
                            data-testid={`agenda-col-${p.id}`}
                            className="px-3 py-3 border-l border-amber-500/15 flex items-center gap-2"
                        >
                            <img src={p.avatar} alt="" className="h-8 w-8 rounded-lg object-cover ring-1 ring-amber-500/30" />
                            <div className="min-w-0">
                                <div className="text-amber-100 text-sm font-semibold truncate">{p.name}</div>
                                <div className="text-[9px] uppercase tracking-wider text-amber-300/50 truncate">{p.role}</div>
                            </div>
                        </div>
                    ))}
                </div>

                {/* Body: slots + cards */}
                <div
                    className="relative grid"
                    style={{
                        gridTemplateColumns: `80px repeat(${activePros.length}, minmax(var(--agenda-col-min-w), 1fr))`,
                        minWidth: `calc(80px + ${activePros.length} * var(--agenda-col-min-w))`,
                    }}
                >
                    {/* Time labels */}
                    <div className="border-r border-amber-500/15">
                        {slotLabels.map((lbl, i) => (
                            <div
                                key={lbl}
                                className={`flex items-start justify-end pr-2 pt-1 text-[10px] font-mono font-bold ${
                                    i % 2 === 0 ? "text-amber-300/80" : "text-amber-300/30"
                                }`}
                                style={{ height: SLOT_HEIGHT_PX }}
                            >
                                {i % 2 === 0 ? lbl : ""}
                            </div>
                        ))}
                    </div>

                    {/* Pro columns */}
                    {activePros.map((pro) => {
                        const proAppts = dayAppts.filter((a) => a.professionalId === pro.id);
                        return (
                            <ProColumn
                                key={pro.id}
                                pro={pro}
                                slotCount={slotLabels.length}
                                appointments={proAppts}
                                conflictPreview={conflictPreview}
                                onWaitlistDrop={handleWaitlistDrop}
                                onEmptyClick={(slotIdx) => {
                                    // Modo "definir horário" da Lista de Espera: o clique
                                    // encaixa a entrada nesse slot (confirma direto).
                                    if (placingEntry) {
                                        handleWaitlistDrop(placingEntry, pro.id, slotIdx);
                                        return;
                                    }
                                    const d = new Date(selectedDate);
                                    const totalMin = slotIdx * SLOT_MINUTES;
                                    d.setHours(SLOT_START_HOUR + Math.floor(totalMin / 60), totalMin % 60, 0, 0);
                                    setEditorState({
                                        mode: "create",
                                        prefill: { date: d.getTime(), professionalId: pro.id },
                                    });
                                }}
                                onCardClick={(appt) => {
                                    if (normalizeApptStatus(appt.status) === "awaiting_confirmation") {
                                        setAwaitingModal(appt);
                                    } else {
                                        setEditorState({ mode: "edit", apptId: appt.id });
                                    }
                                }}
                                onConfirmBooking={(appt) => handleConfirmBooking(appt)}
                                onRefuseBooking={(appt) => {
                                    if (!window.confirm(`Recusar o agendamento de ${appt.clientName || appt.client_name_snapshot || "este cliente"}? O cliente será avisado por WhatsApp.`)) return;
                                    handleRefuseBooking(appt);
                                }}
                                onStart={(appt) => {
                                    startAppointment(appt.id);
                                    toast.success(`${appt.clientName} · Serviço iniciado`, {
                                        description: `${appt.serviceName} · ${appt.duration_min} min previstos`,
                                    });
                                }}
                                onFinish={(appt) => {
                                    finishAppointment(appt.id);
                                    toast.success(`${appt.clientName} · Concluído`);
                                }}
                                onCancel={(appt) => {
                                    if (!window.confirm(`Cancelar agendamento de ${appt.clientName}?`)) return;
                                    cancelAppointment(appt.id);
                                    toast.success("Agendamento cancelado");
                                }}
                            />
                        );
                    })}
                </div>
                </div>
            </DndContext>

            {conflict && (
                <ConflictResolutionModal
                    conflict={conflict}
                    currentPro={professionals.find((p) => p.id === conflict.targetProId)}
                    onClose={() => setConflict(null)}
                    onPickSlot={handleConflictPickSlot}
                    onPickPro={handleConflictPickPro}
                />
            )}

            {awaitingModal && (
                <AwaitingConfirmationModal
                    appt={awaitingModal}
                    client={clients.find((c) => c.id === awaitingModal.clientId) || {
                        name: awaitingModal.client_name_snapshot,
                        phone: awaitingModal.client_phone_snapshot,
                    }}
                    proName={
                        professionals.find((p) => p.id === awaitingModal.professionalId)?.name || "—"
                    }
                    onClose={() => setAwaitingModal(null)}
                    onConfirm={async () => { await handleConfirmBooking(awaitingModal); setAwaitingModal(null); }}
                    onRefuse={async (reason) => { await handleRefuseBooking(awaitingModal, reason); setAwaitingModal(null); }}
                />
            )}

            {/* Lista de Espera — drawer lateral (full-width no mobile) */}
            {waitlistOpen && (
                <div
                    data-testid="agenda-waitlist-panel"
                    className="fixed top-[80px] bottom-4 left-3 right-3 sm:left-auto sm:right-4 z-30 rounded-2xl overflow-hidden shadow-2xl shadow-black/50 border border-amber-500/25 flex"
                    style={{ maxHeight: "calc(100vh - 100px)" }}
                >
                    <div className="relative w-full sm:w-auto">
                        <button
                            data-testid="agenda-waitlist-close"
                            onClick={() => setWaitlistOpen(false)}
                            className="hidden sm:flex absolute -left-9 top-2 z-10 h-8 w-8 rounded-full bg-slate-900 border border-white/10 text-slate-300 hover:text-white hover:bg-slate-800 items-center justify-center"
                            aria-label="Fechar lista de espera"
                        >
                            <i className="fa-solid fa-chevron-right"></i>
                        </button>
                        <WaitlistDrawer
                            refreshTick={waitlistRefresh}
                            pendingAppointments={pendingAppointments}
                            onClose={() => setWaitlistOpen(false)}
                            onConfirmAppointment={async (appt) => {
                                await handleConfirmBooking(appt);
                                setWaitlistRefresh((v) => v + 1);
                            }}
                            onRefuseAppointment={async (appt) => {
                                await handleRefuseBooking(appt);
                                setWaitlistRefresh((v) => v + 1);
                            }}
                            onDefineTime={(entry) => {
                                setPlacingEntry(entry);
                                setWaitlistOpen(false);
                                toast.info("Escolha um horário na agenda", { description: entry.client_name });
                            }}
                            onDragStart={() => { /* highlight will follow via HTML5 native drag */ }}
                            onDragEnd={() => setWaitlistRefresh((v) => v + 1)}
                        />
                    </div>
                </div>
            )}

            {waitlistDropPending && (
                <WaitlistDropConfirmModal
                    pending={waitlistDropPending}
                    professional={professionals.find((p) => p.id === waitlistDropPending.proId)}
                    saving={waitlistDropSaving}
                    onClose={() => !waitlistDropSaving && setWaitlistDropPending(null)}
                    onConfirm={confirmWaitlistDrop}
                />
            )}
        </div>
    );
};

// ─── Nav Card (hub) — mesmo padrão visual do Catálogo & Estoque ────────────
const NavCard = ({ testId, icon, title, description, badge, accent = "from-amber-500/30 to-amber-400/5", onClick }) => (
    <div
        onClick={onClick}
        data-testid={testId}
        role="button"
        tabIndex={0}
        onKeyDown={(e) => {
            if (e.key === "Enter" || e.key === " ") {
                e.preventDefault();
                onClick();
            }
        }}
        className="group relative rounded-2xl border border-amber-500/20 bg-black/50 p-5 text-left overflow-hidden transition-all duration-300 cursor-pointer hover:border-amber-400/55 hover:-translate-y-1 hover:shadow-[0_24px_50px_-20px_rgba(212,175,55,0.45)] active:translate-y-0 active:scale-[0.99]"
    >
        {/* Background gradient */}
        <div className={`absolute inset-0 bg-gradient-to-br ${accent} opacity-60 group-hover:opacity-100 transition-opacity duration-500 pointer-events-none`}></div>

        <div className="relative h-full flex flex-col min-h-[170px]">
            {/* Icon */}
            <div className="h-14 w-14 rounded-2xl flex items-center justify-center border border-amber-400/30 bg-gradient-to-br from-amber-500/25 to-amber-700/5 text-amber-300 transition-all duration-500 group-hover:scale-110 group-hover:rotate-3">
                <i className={`fa-solid ${icon} text-xl`}></i>
            </div>

            {/* Title + subtitle */}
            <div className="mt-auto">
                <h3 className="salon-heading text-lg lg:text-xl text-amber-100 leading-tight">{title}</h3>
                <p className="text-[11px] text-amber-200/55 mt-1 leading-snug">{description}</p>
                {badge && (
                    <span className={`mt-2 inline-block text-[10px] px-2 py-0.5 rounded-full font-bold uppercase tracking-wider ${
                        badge === "red"
                            ? "bg-red-500/20 text-red-300 border border-red-500/30"
                            : "bg-amber-500/20 text-amber-300 border border-amber-500/30"
                    }`}>
                        {badge === "red" ? "Atenção" : "Pendente"}
                    </span>
                )}
            </div>

            {/* Arrow */}
            <div className="absolute bottom-0 right-0 text-amber-300/40 group-hover:text-amber-200 group-hover:translate-x-1 group-hover:-translate-y-1 transition-all">
                <i className="fa-solid fa-arrow-up-right"></i>
            </div>
        </div>

        {/* Bottom hover accent */}
        <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-gradient-to-r from-transparent via-amber-400/60 to-transparent scale-x-0 group-hover:scale-x-100 transition-transform duration-500"></div>
    </div>
);

const BackButton = ({ onClick, testId }) => (
    <button
        data-testid={testId}
        onClick={onClick}
        className="h-10 w-10 rounded-xl border border-white/10 hover:border-amber-500/40 text-slate-400 hover:text-amber-300 flex items-center justify-center transition-all"
        title="Voltar"
    >
        <i className="fa-solid fa-arrow-left"></i>
    </button>
);

// ─── Awaiting confirmation modal ──────────────────────────────────────────
const AwaitingConfirmationModal = ({ appt, client, proName, onClose, onConfirm, onRefuse }) => {
    const [refuseMode, setRefuseMode] = useState(false);
    const [reason, setReason] = useState("");
    return (
        <div
            data-testid="agenda-awaiting-modal"
            className="fixed inset-0 z-[70] flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm animate-in fade-in duration-200"
            onClick={onClose}
        >
            <div
                onClick={(e) => e.stopPropagation()}
                className="w-full max-w-md rounded-2xl border border-amber-400/40 bg-slate-950 shadow-[0_30px_80px_-10px_rgba(251,191,36,0.35)] overflow-hidden"
            >
                <div className="p-5 border-b border-white/5 flex items-start gap-3">
                    <div className="h-12 w-12 rounded-xl flex items-center justify-center bg-amber-500/20 border border-amber-400/40 text-amber-200 text-lg shrink-0 animate-pulse">
                        <i className="fa-solid fa-clock"></i>
                    </div>
                    <div className="flex-1 min-w-0">
                        <div className="text-[10px] uppercase tracking-[0.25em] text-amber-300/80">Booking online</div>
                        <h3 className="salon-heading text-xl text-white mt-0.5">Aguardando confirmação</h3>
                        <p className="text-xs text-slate-400 mt-1">Solicitação recebida via link público.</p>
                    </div>
                </div>

                <div className="p-5 space-y-2.5 text-sm">
                    <InfoLine icon="fa-user" label="Cliente" value={`${client?.name || "—"} · ${client?.phone || ""}`} />
                    <InfoLine icon="fa-calendar" label="Data" value={`${appt.date instanceof Date ? appt.date.toLocaleDateString("pt-BR") : new Date(appt.date).toLocaleDateString("pt-BR")} · ${appt.time || ""}`} />
                    <InfoLine icon="fa-user-tie" label="Profissional" value={proName} />
                    <InfoLine icon="fa-hourglass" label="Duração" value={`${appt.duration_min || 30}min`} />
                    {appt.total ? <InfoLine icon="fa-sack-dollar" label="Total" value={`R$ ${Number(appt.total).toFixed(2)}`} /> : null}
                    {appt.notes ? <InfoLine icon="fa-comment" label="Observação" value={appt.notes} /> : null}
                </div>

                {refuseMode && (
                    <div className="px-5 pb-3">
                        <label className="block text-[10px] uppercase tracking-wider text-amber-300/70 font-bold mb-1">
                            Motivo (opcional)
                        </label>
                        <input
                            data-testid="agenda-awaiting-reason"
                            value={reason}
                            onChange={(e) => setReason(e.target.value)}
                            placeholder="Ex.: horário indisponível"
                            className="w-full min-h-[40px] rounded-lg bg-black/60 border border-red-500/25 px-3 text-sm text-white outline-none focus:border-red-400/60"
                            autoFocus
                        />
                    </div>
                )}

                <div className="p-4 border-t border-white/5 flex items-center justify-end gap-2 bg-black/40">
                    {!refuseMode ? (
                        <>
                            <button
                                data-testid="agenda-awaiting-refuse-open"
                                onClick={() => setRefuseMode(true)}
                                className="min-h-[44px] px-4 rounded-xl border border-red-400/50 hover:bg-red-500/15 text-red-200 text-xs uppercase tracking-wider font-bold transition-all inline-flex items-center gap-2"
                            >
                                <i className="fa-solid fa-xmark"></i>Recusar
                            </button>
                            <button
                                data-testid="agenda-awaiting-confirm"
                                onClick={onConfirm}
                                className="min-h-[44px] px-4 rounded-xl bg-emerald-500 hover:bg-emerald-400 text-white text-xs uppercase tracking-wider font-bold transition-all inline-flex items-center gap-2"
                            >
                                <i className="fa-solid fa-check"></i>Confirmar
                            </button>
                        </>
                    ) : (
                        <>
                            <button
                                onClick={() => { setRefuseMode(false); setReason(""); }}
                                className="min-h-[44px] px-4 rounded-xl border border-white/10 hover:border-white/30 text-slate-200 text-xs uppercase tracking-wider font-bold"
                            >
                                Voltar
                            </button>
                            <button
                                data-testid="agenda-awaiting-refuse-confirm"
                                onClick={() => onRefuse(reason)}
                                className="min-h-[44px] px-4 rounded-xl bg-red-500 hover:bg-red-400 text-white text-xs uppercase tracking-wider font-bold inline-flex items-center gap-2"
                            >
                                <i className="fa-solid fa-xmark"></i>Confirmar recusa
                            </button>
                        </>
                    )}
                </div>
            </div>
        </div>
    );
};

// ─── Waitlist Drop Confirm Modal ──────────────────────────────────────────
const WaitlistDropConfirmModal = ({ pending, professional, saving, onClose, onConfirm }) => {
    if (!pending) return null;
    const { entry, dateObj, dateStr, timeStr } = pending;
    const displayDate = dateObj
        ? dateObj.toLocaleDateString("pt-BR", { weekday: "long", day: "2-digit", month: "long" })
        : dateStr;
    return (
        <div
            data-testid="waitlist-drop-modal"
            className="fixed inset-0 z-[80] flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm animate-in fade-in duration-200"
            onClick={onClose}
        >
            <div
                onClick={(e) => e.stopPropagation()}
                className="w-full max-w-md rounded-2xl border border-amber-400/40 bg-slate-950 shadow-[0_30px_80px_-10px_rgba(251,191,36,0.35)] overflow-hidden"
            >
                <div className="p-5 border-b border-white/5 flex items-start gap-3">
                    <div className="h-12 w-12 rounded-xl flex items-center justify-center bg-amber-500/20 border border-amber-400/40 text-amber-200 text-lg shrink-0">
                        <i className="fa-solid fa-hourglass-half"></i>
                    </div>
                    <div className="flex-1 min-w-0">
                        <div className="text-[10px] uppercase tracking-[0.25em] text-amber-300/80">Lista de espera</div>
                        <h3 className="salon-heading text-xl text-white mt-0.5">Confirmar encaixe?</h3>
                        <p className="text-xs text-slate-400 mt-1">Cliente será agendado direto neste horário.</p>
                    </div>
                </div>

                <div className="p-5 space-y-3">
                    <InfoLine icon="fa-user" label="Cliente" value={entry.client_name} />
                    <InfoLine icon="fa-phone" label="Telefone" value={entry.client_phone} />
                    <InfoLine icon="fa-scissors" label="Serviço" value={entry.service_names || "—"} />
                    <InfoLine icon="fa-calendar-day" label="Data" value={displayDate} />
                    <InfoLine icon="fa-clock" label="Horário" value={timeStr} />
                    <InfoLine icon="fa-user-tie" label="Profissional" value={professional?.name || "—"} />
                </div>

                <div className="p-4 border-t border-white/5 bg-black/40 flex gap-2 justify-end">
                    <button
                        type="button"
                        data-testid="waitlist-drop-cancel"
                        onClick={onClose}
                        disabled={saving}
                        className="h-10 px-4 rounded-lg border border-white/10 text-slate-300 text-xs uppercase tracking-widest hover:bg-white/5 disabled:opacity-50"
                    >
                        Cancelar
                    </button>
                    <button
                        type="button"
                        data-testid="waitlist-drop-confirm"
                        onClick={onConfirm}
                        disabled={saving}
                        className="h-10 px-4 rounded-lg bg-amber-500 hover:bg-amber-400 text-slate-950 font-semibold text-xs uppercase tracking-widest disabled:opacity-50 flex items-center gap-2"
                    >
                        {saving ? (
                            <>
                                <i className="fa-solid fa-spinner fa-spin"></i>
                                Agendando...
                            </>
                        ) : (
                            <>
                                <i className="fa-solid fa-check"></i>
                                Confirmar agendamento
                            </>
                        )}
                    </button>
                </div>
            </div>
        </div>
    );
};

const InfoLine = ({ icon, label, value }) => (
    <div className="flex items-start gap-3">
        <div className="h-7 w-7 rounded-md flex items-center justify-center bg-white/5 text-amber-300 text-xs shrink-0">
            <i className={`fa-solid ${icon}`}></i>
        </div>
        <div className="flex-1 min-w-0">
            <div className="text-[9px] uppercase tracking-[0.2em] text-slate-500 font-bold">{label}</div>
            <div className="text-sm text-white break-words">{value}</div>
        </div>
    </div>
);

// ─── Pro Column ───────────────────────────────────────────────────────────
const ProColumn = ({ pro, slotCount, appointments, conflictPreview, onEmptyClick, onCardClick, onConfirmBooking, onRefuseBooking, onStart, onFinish, onCancel, onWaitlistDrop }) => {
    return (
        <div className="relative border-l border-amber-500/15" data-testid={`agenda-col-body-${pro.id}`}>
            {/* Slot backgrounds (clickable to create + droppable for drag) */}
            {Array.from({ length: slotCount }, (_, i) => (
                <DroppableSlot
                    key={i}
                    proId={pro.id}
                    slotIdx={i}
                    isConflict={conflictPreview === `${pro.id}-${i}`}
                    onEmptyClick={() => onEmptyClick(i)}
                    onWaitlistDrop={onWaitlistDrop}
                />
            ))}

            {/* Overlay cards — ordenados por início pra calcular o espaço até
                o próximo card (o card "AGUARDA" só cresce até onde couber). */}
            {[...appointments]
                .map((a) => ({ a, startSlot: dateToSlotIndex(a.date), span: minutesToSlots(a.duration_min || 30) }))
                .filter((x) => x.startSlot >= 0 && x.startSlot < slotCount)
                .sort((x, y) => x.startSlot - y.startSlot)
                .map(({ a, startSlot, span }, idx, arr) => {
                    const rawHeight = span * SLOT_HEIGHT_PX - 4;
                    const nextStart = arr[idx + 1]?.startSlot ?? slotCount;
                    const roomHeight = (nextStart - startSlot) * SLOT_HEIGHT_PX - 4; // até o próximo card
                    const st = normalizeApptStatus(a.status);
                    // Card "AGUARDA" quer altura mínima pros dois botões, MAS nunca
                    // invade o card seguinte — se o próximo slot já está ocupado,
                    // fica na altura natural e usa o layout enxuto (1 linha + botões).
                    const needsRoom = st === "awaiting_confirmation";
                    const height = needsRoom
                        ? Math.min(Math.max(rawHeight, ACTION_CARD_MIN_H), Math.max(rawHeight, roomHeight))
                        : rawHeight;
                    const elevated = needsRoom && height > rawHeight + 1;
                    return (
                        <DraggableCard
                            key={a.id}
                            appt={a}
                            top={startSlot * SLOT_HEIGHT_PX}
                            height={height}
                            elevated={elevated}
                        >
                            <AppointmentCard
                                appt={a}
                                height={height}
                                onClick={() => onCardClick(a)}
                                onStart={() => onStart(a)}
                                onFinish={() => onFinish(a)}
                                onCancel={() => onCancel(a)}
                                onConfirmBooking={() => onConfirmBooking(a)}
                                onRefuseBooking={() => onRefuseBooking(a)}
                            />
                        </DraggableCard>
                    );
                })}
        </div>
    );
};

// ─── DnD Helpers ──────────────────────────────────────────────────────────
const DroppableSlot = ({ proId, slotIdx, isConflict, onEmptyClick, onWaitlistDrop }) => {
    const { setNodeRef, isOver } = useDroppable({
        id: `slot-${proId}-${slotIdx}`,
        data: { kind: "slot", proId, slotIdx },
    });
    const [waitlistOver, setWaitlistOver] = useState(false);
    const showConflict = isOver && isConflict;

    const handleDragOverNative = (ev) => {
        // HTML5 drag: aceita apenas se for uma entrada de waitlist
        if (ev.dataTransfer.types.includes("application/x-kinng-waitlist")) {
            ev.preventDefault();
            ev.dataTransfer.dropEffect = "move";
            if (!waitlistOver) setWaitlistOver(true);
        }
    };
    const handleDragLeaveNative = () => setWaitlistOver(false);
    const handleDropNative = (ev) => {
        setWaitlistOver(false);
        const raw = ev.dataTransfer.getData("application/x-kinng-waitlist");
        if (!raw) return;
        ev.preventDefault();
        try {
            const entry = JSON.parse(raw);
            onWaitlistDrop && onWaitlistDrop(entry, proId, slotIdx);
        } catch (_) { /* ignore */ }
    };

    return (
        <button
            ref={setNodeRef}
            data-testid={`agenda-slot-${proId}-${slotIdx}`}
            data-conflict={showConflict ? "true" : "false"}
            type="button"
            onClick={onEmptyClick}
            onDragOver={handleDragOverNative}
            onDragLeave={handleDragLeaveNative}
            onDrop={handleDropNative}
            className={`block w-full border-b border-dashed transition-colors relative ${
                waitlistOver
                    ? "bg-amber-500/25 border-amber-400"
                    : showConflict
                        ? "bg-red-500/15 border-red-500/60 animate-pulse"
                        : isOver
                            ? "bg-emerald-500/15 border-emerald-400/40"
                            : (slotIdx % 2 === 0 ? "border-amber-500/8 hover:bg-amber-500/5" : "border-amber-500/8 bg-black/20 hover:bg-amber-500/5")
            }`}
            style={{ height: SLOT_HEIGHT_PX }}
        >
            {waitlistOver && (
                <span className="absolute inset-y-0 left-2 flex items-center text-[10px] uppercase tracking-wider font-bold text-amber-200 pointer-events-none">
                    <i className="fa-solid fa-hourglass-half mr-1"></i>
                    Soltar aqui pra agendar
                </span>
            )}
            {showConflict && (
                <span className="absolute inset-y-0 left-2 flex items-center text-[10px] uppercase tracking-wider font-bold text-red-300 pointer-events-none">
                    <i className="fa-solid fa-triangle-exclamation mr-1"></i>
                    Conflito
                </span>
            )}
        </button>
    );
};

const DraggableCard = ({ appt, children, top, height, elevated }) => {
    const { attributes, listeners, setNodeRef, transform, isDragging } = useDraggable({
        id: appt.id,
        data: { kind: "appointment", apptId: appt.id },
    });
    const style = {
        top: top + 2,
        height,
        transform: transform ? `translate3d(${transform.x}px, ${transform.y}px, 0)` : undefined,
        opacity: isDragging ? 0.5 : 1,
        zIndex: isDragging ? 30 : elevated ? 5 : 1,
    };
    return (
        <div ref={setNodeRef} style={style} className="absolute left-1 right-1" {...attributes} {...listeners}>
            {children}
        </div>
    );
};

// ─── Appointment Card (inside Timeline) ───────────────────────────────────
// Botão de ação, base + verde/vermelho/azul. Sempre visível (nada de hover)
// pros estados que exigem decisão: AGUARDA (Confirmar/Recusar) e agendado
// (Iniciar). "em-andamento"/"concluído" continuam como antes.
const cardBtn = (tone) =>
    `flex-1 inline-flex items-center justify-center gap-1 rounded-md border font-bold uppercase tracking-wider transition-colors ${
        {
            emerald: "border-emerald-400/60 bg-emerald-500/20 text-emerald-100 hover:bg-emerald-500/35 active:bg-emerald-500/45",
            red: "border-red-400/55 bg-red-500/20 text-red-100 hover:bg-red-500/35 active:bg-red-500/45",
            sky: "border-sky-400/55 bg-sky-500/20 text-sky-50 hover:bg-sky-500/35 active:bg-sky-500/45",
        }[tone]
    }`;

const AppointmentCard = ({ appt, height, onClick, onStart, onFinish, onCancel, onConfirmBooking, onRefuseBooking }) => {
    const status = normalizeApptStatus(appt.status);
    const palette = STATUS_PALETTE[status] || STATUS_PALETTE.agendado;
    const isAwaiting = status === "awaiting_confirmation";
    const isScheduled = status === "agendado";
    const clientLabel = appt.clientName || appt.client_name_snapshot || "Cliente";
    const stop = (e) => e.stopPropagation();

    // ─── Estados que exigem decisão: botões SEMPRE visíveis (sem hover),
    //     centralizados e sem o "✕" solto. AGUARDA → Confirmar/Recusar;
    //     confirmado (agendado) → Iniciar.
    if (isAwaiting || isScheduled) {
        // agendado de 30min = card baixo → layout enxuto (1 linha + botão).
        // O card AGUARDA recebe altura mínima maior (ProColumn) → nunca compacto.
        const isCompact = typeof height === "number" && height < 84;
        return (
            <div
                data-testid={`agenda-card-${appt.id}`}
                data-status={appt.status}
                className={`relative w-full h-full rounded-lg border ${palette.chipBorder} ${palette.chipBg} backdrop-blur-md cursor-grab active:cursor-grabbing overflow-hidden group hover:shadow-[0_8px_24px_-12px_rgba(212,175,55,0.5)] transition-all flex flex-col ${isAwaiting ? "ring-2 ring-amber-300/70 shadow-[0_0_24px_-4px_rgba(251,191,36,0.45)]" : ""}`}
                style={{
                    borderLeftWidth: 4,
                    // O card AGUARDA transborda o slot — fundo opaco pra não
                    // "vazar" com o card de baixo.
                    ...(isAwaiting ? { backgroundColor: "rgba(24, 18, 6, 0.94)" } : {}),
                }}
                onClick={onClick}
            >
                <div className={`flex-1 flex flex-col min-h-0 overflow-hidden ${isCompact ? "p-1 gap-0.5" : "p-2 gap-0.5"}`}>
                    {isCompact ? (
                        <div className="flex items-center gap-1 min-w-0">
                            <span className={`font-mono text-[10px] font-bold shrink-0 ${palette.chipText}`}>{appt.time}</span>
                            <span className={`text-[10px] font-semibold truncate ${palette.chipText}`}>{clientLabel}</span>
                            {isAwaiting && (
                                <i
                                    data-testid={`agenda-card-awaiting-badge-${appt.id}`}
                                    className="fa-solid fa-clock text-[9px] text-amber-300 shrink-0 ml-auto animate-pulse"
                                    title="Aguardando confirmação"
                                ></i>
                            )}
                        </div>
                    ) : (
                        <>
                            <div className="flex items-baseline justify-between gap-2">
                                <span className={`font-mono text-[11px] font-bold ${palette.chipText}`}>{appt.time}</span>
                                {isAwaiting ? (
                                    <span
                                        data-testid={`agenda-card-awaiting-badge-${appt.id}`}
                                        title="Aguardando confirmação"
                                        className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[9px] uppercase tracking-wider font-bold bg-amber-400/30 text-amber-100 border border-amber-300/60 animate-pulse"
                                    >
                                        <i className="fa-solid fa-clock"></i>Aguarda
                                    </span>
                                ) : (
                                    <span className="text-[9px] uppercase tracking-wider text-amber-200/60 font-mono">
                                        {formatDuration(appt.duration_min)}
                                    </span>
                                )}
                            </div>
                            <div className={`text-xs font-semibold truncate ${palette.chipText}`}>{clientLabel}</div>
                            <div className="text-[11px] truncate opacity-80" style={{ color: palette.chipText }}>
                                {appt.serviceName}
                            </div>
                        </>
                    )}

                    <div className={`mt-auto flex items-center justify-center gap-1.5 ${isCompact ? "pt-0.5" : "pt-1"}`} onClick={stop}>
                        {isAwaiting ? (
                            <>
                                <button
                                    data-testid={`agenda-card-confirm-${appt.id}`}
                                    onClick={onConfirmBooking}
                                    className={`${cardBtn("emerald")} ${isCompact ? "text-[8px] px-1 py-0.5" : "text-[9px] px-1.5 py-1"}`}
                                >
                                    <i className="fa-solid fa-check"></i>Confirmar
                                </button>
                                <button
                                    data-testid={`agenda-card-refuse-${appt.id}`}
                                    onClick={onRefuseBooking}
                                    className={`${cardBtn("red")} ${isCompact ? "text-[8px] px-1 py-0.5" : "text-[9px] px-1.5 py-1"}`}
                                >
                                    <i className="fa-solid fa-xmark"></i>Recusar
                                </button>
                            </>
                        ) : (
                            <button
                                data-testid={`agenda-card-start-${appt.id}`}
                                onClick={onStart}
                                className={`${cardBtn("sky")} ${isCompact ? "text-[9px] px-2 py-0.5" : "text-[10px] px-2 py-1"}`}
                            >
                                <i className="fa-solid fa-play"></i>Iniciar
                            </button>
                        )}
                    </div>
                </div>
            </div>
        );
    }

    // ─── Demais estados (em-andamento, concluído): comportamento anterior ───
    const isCompact = false;
    return (
        <div
            data-testid={`agenda-card-${appt.id}`}
            data-status={appt.status}
            className={`relative w-full h-full rounded-lg border ${palette.chipBorder} ${palette.chipBg} backdrop-blur-sm cursor-grab active:cursor-grabbing overflow-hidden group hover:shadow-[0_8px_24px_-12px_rgba(212,175,55,0.5)] transition-all flex flex-col`}
            style={{ borderLeftWidth: 4 }}
            onClick={onClick}
        >
            <div className="flex-1 p-2 flex flex-col gap-0.5 min-h-0 overflow-hidden">
                <div className="flex items-baseline justify-between gap-2">
                    <span className={`font-mono text-[11px] font-bold ${palette.chipText}`}>{appt.time}</span>
                    <span className="text-[9px] uppercase tracking-wider text-amber-200/60 font-mono">
                        {formatDuration(appt.duration_min)}
                    </span>
                </div>
                <div className={`text-xs font-semibold truncate ${palette.chipText}`}>{clientLabel}</div>
                {!isCompact && (
                    <div className="text-[11px] truncate opacity-80" style={{ color: palette.chipText }}>
                        {appt.serviceName}
                    </div>
                )}
                {!isCompact && status === "em-andamento" && (
                    <div className="mt-auto pt-1">
                        <TickProgressBar appointment={appt} variant="thin" testId={`agenda-card-progress-${appt.id}`} />
                    </div>
                )}
                <div className={`flex items-center gap-1 mt-auto ${isCompact ? "hidden" : "opacity-0 group-hover:opacity-100"} transition-opacity`} onClick={stop}>
                    {status === "em-andamento" && (
                        <button
                            data-testid={`agenda-card-finish-${appt.id}`}
                            onClick={onFinish}
                            className="text-[9px] uppercase tracking-wider font-bold px-2 py-1 rounded-md border border-emerald-400/40 bg-emerald-500/15 text-emerald-200 hover:bg-emerald-500/25"
                        >
                            <i className="fa-solid fa-check mr-1"></i>Concluir
                        </button>
                    )}
                    {status !== "concluido" && (
                        <button
                            data-testid={`agenda-card-cancel-${appt.id}`}
                            onClick={onCancel}
                            className="text-[9px] uppercase tracking-wider font-bold px-2 py-1 rounded-md border border-red-400/30 text-red-300 hover:bg-red-500/15"
                        >
                            <i className="fa-solid fa-xmark"></i>
                        </button>
                    )}
                </div>
            </div>
            {status === "em-andamento" && isCompact && (
                <div className="px-1.5 pb-1">
                    <TickProgressBar appointment={appt} variant="thin" testId={`agenda-card-progress-${appt.id}`} />
                </div>
            )}
        </div>
    );
};

// ─── Fullscreen Editor (página interna, NÃO modal) ────────────────────────
export const AppointmentEditor = ({ state, onClose, defaultDate }) => {
    const { brand, professionals, clients, services, appointments, upsertAppointment, deleteAppointment, startAppointment, finishAppointment } = useSalon();
    const salonHours = brand?.salonHours;

    const existing = state.mode === "edit" ? appointments.find((a) => a.id === state.apptId) : null;
    const prefill = state.prefill || {};

    // Form state
    const initialDate = existing?.date || prefill.date || defaultDate.getTime();
    const initialDateObj = new Date(initialDate);

    const [form, setForm] = useState(() => ({
        clientId: existing?.clientId || prefill.clientId || "",
        clientName: existing?.clientName || prefill.clientName || "",
        professionalId: existing?.professionalId || prefill.professionalId || professionals[0]?.id || "",
        serviceId: existing?.serviceId || prefill.serviceId || services[0]?.id || "",
        dateStr: `${initialDateObj.getFullYear()}-${String(initialDateObj.getMonth() + 1).padStart(2, "0")}-${String(initialDateObj.getDate()).padStart(2, "0")}`,
        timeStr: `${String(initialDateObj.getHours()).padStart(2, "0")}:${String(initialDateObj.getMinutes()).padStart(2, "0")}`,
        notes: existing?.notes || "",
        walkIn: !existing?.clientId && Boolean(existing?.clientName),
    }));

    const [clientQuery, setClientQuery] = useState("");

    const selectedService = services.find((s) => s.id === form.serviceId);
    const selectedPro = professionals.find((p) => p.id === form.professionalId);
    const selectedClient = clients.find((c) => c.id === form.clientId);

    const filteredClients = useMemo(() => {
        if (!clientQuery.trim()) return clients.slice(0, 12);
        const q = clientQuery.toLowerCase();
        return clients.filter((c) => {
            const ph = (c.phone || "").replace(/\D/g, "");
            return c.name.toLowerCase().includes(q) || ph.includes(clientQuery.replace(/\D/g, ""));
        });
    }, [clients, clientQuery]);

    // Computed estimated end
    const computedDate = useMemo(() => {
        const [y, m, d] = form.dateStr.split("-").map(Number);
        const [hh, mm] = form.timeStr.split(":").map(Number);
        return new Date(y, m - 1, d, hh, mm, 0, 0);
    }, [form.dateStr, form.timeStr]);

    const duration = selectedService?.duration || 30;
    const estimatedEnd = new Date(computedDate.getTime() + duration * 60000);

    // Conflict detection
    const conflicts = useMemo(() => {
        const startTs = computedDate.getTime();
        const endTs = startTs + duration * 60000;
        return appointments.filter((a) => {
            if (existing && a.id === existing.id) return false;
            if (isCancelledStatus(a.status)) return false;
            if (a.professionalId !== form.professionalId) return false;
            const aEnd = a.estimated_end || a.date + (a.duration_min || 30) * 60000;
            return a.date < endTs && startTs < aEnd;
        });
    }, [appointments, computedDate, duration, form.professionalId, existing]);

    const handleSave = () => {
        const finalClientName = form.walkIn ? form.clientName.trim() : selectedClient?.name || "";
        if (!finalClientName) {
            toast.error("Informe o cliente (cadastrado ou avulso).");
            return;
        }
        if (!selectedService) {
            toast.error("Selecione um serviço.");
            return;
        }
        if (!selectedPro) {
            toast.error("Selecione um colaborador.");
            return;
        }
        // Janela da grade (08:00–20:00): fora disso o card não aparece na Timeline.
        const hourNum = computedDate.getHours();
        if (hourNum < SLOT_START_HOUR || hourNum >= SLOT_END_HOUR) {
            const proceed = window.confirm(
                `⏰ Horário ${form.timeStr} está fora da janela da grade (${String(SLOT_START_HOUR).padStart(2, "0")}:00 às ${String(SLOT_END_HOUR).padStart(2, "0")}:00). O card não aparecerá na grade da Agenda. Salvar mesmo assim?`
            );
            if (!proceed) return;
        }
        // Expediente configurado do tenant (mesma config da navegação / booking público).
        const dh = getDayHours(salonHours, computedDate);
        if (!isDayOpen(salonHours, computedDate) || form.timeStr < dh.from || form.timeStr >= dh.to) {
            const proceed = window.confirm(
                `⏰ ${form.timeStr} está fora do expediente configurado para esse dia (${dh.from}–${dh.to}). Agendar mesmo assim?`
            );
            if (!proceed) return;
        }
        if (conflicts.length > 0) {
            const proceed = window.confirm(
                `⚠️ Há ${conflicts.length} conflito(s) de horário com ${selectedPro.name}. Deseja salvar mesmo assim?`
            );
            if (!proceed) return;
        }

        const id = existing?.id || `appt-${Date.now()}`;
        upsertAppointment({
            id,
            date: computedDate.getTime(),
            time: form.timeStr,
            duration_min: duration,
            estimated_end: estimatedEnd.getTime(),
            clientId: form.walkIn ? null : form.clientId,
            clientName: finalClientName,
            professionalId: form.professionalId,
            professionalName: selectedPro.name,
            serviceId: selectedService.id,
            serviceName: selectedService.name,
            servicePrice: selectedService.price,
            status: existing?.status || "agendado",
            started_at: existing?.started_at || null,
            finished_at: existing?.finished_at || null,
            source: existing?.source || "agenda",
            notes: form.notes,
        });

        toast.success(existing ? "Agendamento atualizado" : "Agendamento criado", {
            description: `${selectedPro.name} · ${form.timeStr} · ${formatDuration(duration)}`,
        });
        onClose();
    };

    const handleDelete = () => {
        if (!existing) return;
        if (!window.confirm(`Excluir agendamento de ${existing.clientName}?`)) return;
        deleteAppointment(existing.id);
        toast.success("Agendamento removido");
        onClose();
    };

    return (
        <div data-testid="agenda-editor" className="fixed inset-0 z-30 bg-[#0a0a0a] overflow-y-auto">
            {/* Editor header bar */}
            <header className="sticky top-0 z-10 backdrop-blur-xl bg-black/80 border-b border-amber-500/20 px-6 py-4 flex items-center gap-4">
                <button
                    data-testid="agenda-editor-back"
                    onClick={onClose}
                    className="min-h-[44px] px-4 rounded-xl border border-amber-500/30 hover:border-amber-500/60 text-amber-200 text-[11px] uppercase tracking-wider font-bold flex items-center gap-2 transition-all"
                >
                    <i className="fa-solid fa-arrow-left"></i>
                    Voltar à Agenda
                </button>
                <div className="flex-1">
                    <h1 className="gold-text salon-heading text-xl">
                        {existing ? "Editar Agendamento" : "Novo Agendamento"}
                    </h1>
                    <p className="text-[10px] uppercase tracking-[0.2em] text-amber-300/60 mt-0.5">
                        {existing ? `ID ${existing.id.slice(0, 16)}…` : "Hub central · sincroniza com Colaborador e Balcão"}
                    </p>
                </div>
                <div className="hidden md:flex items-center gap-2">
                    {existing && (
                        <button
                            data-testid="agenda-editor-delete"
                            onClick={handleDelete}
                            className="min-h-[44px] px-4 rounded-xl border border-red-500/30 hover:border-red-500/60 text-red-300 text-[11px] uppercase tracking-wider font-bold transition-all"
                        >
                            <i className="fa-solid fa-trash mr-2"></i>Excluir
                        </button>
                    )}
                    <button
                        data-testid="agenda-editor-save"
                        onClick={handleSave}
                        className="gold-pill gold-pill-active min-h-[44px] px-6 rounded-xl text-[11px] font-bold uppercase tracking-[0.15em]"
                    >
                        <i className="fa-solid fa-floppy-disk mr-2"></i>
                        Salvar Agendamento
                    </button>
                </div>
            </header>

            <div className="max-w-6xl mx-auto px-6 py-6 grid lg:grid-cols-[1.3fr_1fr] gap-6">
                {/* LEFT: cliente + serviço + colaborador */}
                <div className="space-y-5">
                    {/* Cliente */}
                    <section data-testid="editor-client-section" className="gold-panel p-5">
                        <h3 className="gold-text text-sm uppercase tracking-wider pb-3 gold-border-bottom">
                            <i className="fa-solid fa-user mr-2"></i>Cliente
                        </h3>
                        <div className="mt-3 flex items-center gap-2">
                            <label className="flex items-center gap-2 text-[11px] uppercase tracking-wider text-amber-200/80 cursor-pointer">
                                <input
                                    data-testid="editor-walkin-toggle"
                                    type="checkbox"
                                    checked={form.walkIn}
                                    onChange={(e) => setForm({
                                        ...form,
                                        walkIn: e.target.checked,
                                        clientId: e.target.checked ? "" : form.clientId,
                                        clientName: e.target.checked ? "" : form.clientName,
                                    })}
                                    className="accent-amber-400 h-4 w-4"
                                />
                                Cliente avulso (walk-in)
                            </label>
                        </div>

                        {form.walkIn ? (
                            <input
                                data-testid="editor-walkin-name"
                                value={form.clientName}
                                onChange={(e) => setForm({ ...form, clientName: e.target.value })}
                                placeholder="Nome do cliente avulso"
                                className="mt-3 w-full bg-black/60 border border-amber-500/25 rounded-xl px-3.5 py-2.5 text-amber-100 text-sm outline-none focus:border-amber-400/55 min-h-[44px]"
                            />
                        ) : (
                            <>
                                <input
                                    data-testid="editor-client-search"
                                    value={clientQuery}
                                    onChange={(e) => setClientQuery(e.target.value)}
                                    placeholder="Buscar cliente por nome ou telefone..."
                                    className="mt-3 w-full bg-black/60 border border-amber-500/25 rounded-xl px-3.5 py-2.5 text-amber-100 text-sm outline-none focus:border-amber-400/55 min-h-[44px]"
                                />
                                <div className="mt-3 grid grid-cols-2 gap-2 max-h-[260px] overflow-y-auto pr-1">
                                    {filteredClients.length === 0 && (
                                        <p className="col-span-2 text-center text-amber-200/40 text-xs py-6">
                                            Nenhum cliente encontrado.
                                        </p>
                                    )}
                                    {filteredClients.map((c) => {
                                        const active = c.id === form.clientId;
                                        return (
                                            <button
                                                key={c.id}
                                                data-testid={`editor-client-opt-${c.id}`}
                                                onClick={() => setForm({ ...form, clientId: c.id, clientName: c.name })}
                                                className={`text-left rounded-xl border p-2.5 transition-all min-h-[60px] ${
                                                    active
                                                        ? "border-amber-400/60 bg-amber-500/15"
                                                        : "border-amber-500/15 bg-black/40 hover:border-amber-500/35"
                                                }`}
                                            >
                                                <div className="flex items-center gap-2">
                                                    <div className="h-8 w-8 rounded-lg flex items-center justify-center bg-amber-500/10 border border-amber-500/30 text-amber-300 text-xs font-bold">
                                                        {c.name.charAt(0).toUpperCase()}
                                                    </div>
                                                    <div className="min-w-0">
                                                        <div className="text-xs font-semibold text-amber-100 truncate">{c.name}</div>
                                                        <div className="text-[10px] text-amber-300/60 font-mono truncate">{c.phone}</div>
                                                    </div>
                                                </div>
                                            </button>
                                        );
                                    })}
                                </div>
                            </>
                        )}
                    </section>

                    {/* Serviço */}
                    <section data-testid="editor-service-section" className="gold-panel p-5">
                        <h3 className="gold-text text-sm uppercase tracking-wider pb-3 gold-border-bottom flex items-center justify-between">
                            <span><i className="fa-solid fa-scissors mr-2"></i>Serviço</span>
                            {selectedService && (
                                <span className="text-[10px] text-amber-300/70 font-mono">
                                    {formatDuration(selectedService.duration)} · R$ {selectedService.price}
                                </span>
                            )}
                        </h3>
                        <div className="mt-3 grid grid-cols-2 md:grid-cols-3 gap-2 max-h-[300px] overflow-y-auto pr-1">
                            {services.filter((s) => s.active).map((svc) => {
                                const active = svc.id === form.serviceId;
                                return (
                                    <button
                                        key={svc.id}
                                        data-testid={`editor-service-opt-${svc.id}`}
                                        onClick={() => setForm({ ...form, serviceId: svc.id })}
                                        className={`rounded-xl border p-3 text-left transition-all min-h-[80px] ${
                                            active
                                                ? "border-amber-400/60 bg-amber-500/15 shadow-[0_0_22px_-10px_rgba(212,175,55,0.6)]"
                                                : "border-amber-500/15 bg-black/40 hover:border-amber-500/40"
                                        }`}
                                    >
                                        <div className="flex items-center justify-between">
                                            <i className={`fa-solid ${svc.icon} text-amber-300`}></i>
                                            <span className="text-[9px] uppercase tracking-wider text-amber-200/60 font-mono">
                                                {formatDuration(svc.duration)}
                                            </span>
                                        </div>
                                        <div className="mt-2 text-xs font-bold text-amber-100 truncate">{svc.name}</div>
                                        <div className="text-[11px] text-emerald-300 font-mono font-semibold">R$ {svc.price}</div>
                                    </button>
                                );
                            })}
                        </div>
                    </section>

                    {/* Colaborador */}
                    <section data-testid="editor-pro-section" className="gold-panel p-5">
                        <h3 className="gold-text text-sm uppercase tracking-wider pb-3 gold-border-bottom">
                            <i className="fa-solid fa-user-tie mr-2"></i>Colaborador
                        </h3>
                        <div className="mt-3 grid grid-cols-2 md:grid-cols-3 gap-2">
                            {professionals.filter((p) => p.active !== false).map((p) => {
                                const active = p.id === form.professionalId;
                                return (
                                    <button
                                        key={p.id}
                                        data-testid={`editor-pro-opt-${p.id}`}
                                        onClick={() => setForm({ ...form, professionalId: p.id })}
                                        className={`rounded-xl border p-3 text-left transition-all min-h-[64px] flex items-center gap-2 ${
                                            active
                                                ? "border-amber-400/60 bg-amber-500/15"
                                                : "border-amber-500/15 bg-black/40 hover:border-amber-500/40"
                                        }`}
                                    >
                                        <img src={p.avatar} alt="" className="h-8 w-8 rounded-lg object-cover ring-1 ring-amber-500/30" />
                                        <div className="min-w-0">
                                            <div className="text-xs font-bold text-amber-100 truncate">{p.name}</div>
                                            <div className="text-[10px] text-amber-300/60 truncate">{p.role}</div>
                                        </div>
                                    </button>
                                );
                            })}
                        </div>
                    </section>
                </div>

                {/* RIGHT: data/hora + resumo */}
                <div className="space-y-5 lg:sticky lg:top-[88px] self-start">
                    <section data-testid="editor-when-section" className="gold-panel p-5">
                        <h3 className="gold-text text-sm uppercase tracking-wider pb-3 gold-border-bottom">
                            <i className="fa-solid fa-calendar-day mr-2"></i>Data & Horário
                        </h3>
                        <div className="mt-3 grid grid-cols-2 gap-3">
                            <label className="block">
                                <span className="text-[10px] uppercase tracking-wider text-amber-300/70 font-bold">Data</span>
                                <input
                                    data-testid="editor-date"
                                    type="date"
                                    value={form.dateStr}
                                    onChange={(e) => setForm({ ...form, dateStr: e.target.value })}
                                    className="mt-1.5 w-full bg-black/60 border border-amber-500/25 rounded-xl px-3 py-2.5 text-amber-100 text-sm font-mono outline-none focus:border-amber-400/55 min-h-[44px]"
                                />
                            </label>
                            <label className="block">
                                <span className="text-[10px] uppercase tracking-wider text-amber-300/70 font-bold">Horário</span>
                                <input
                                    data-testid="editor-time"
                                    type="time"
                                    value={form.timeStr}
                                    onChange={(e) => setForm({ ...form, timeStr: e.target.value })}
                                    className="mt-1.5 w-full bg-black/60 border border-amber-500/25 rounded-xl px-3 py-2.5 text-amber-100 text-sm font-mono outline-none focus:border-amber-400/55 min-h-[44px]"
                                />
                            </label>
                        </div>
                        <div className="mt-4 rounded-xl border border-amber-500/15 bg-black/60 p-3 flex items-center justify-between">
                            <div>
                                <div className="text-[10px] uppercase tracking-wider text-amber-300/60">Término previsto</div>
                                <div className="font-mono text-amber-200 text-lg font-bold" data-testid="editor-estimated-end">
                                    {String(estimatedEnd.getHours()).padStart(2, "0")}:{String(estimatedEnd.getMinutes()).padStart(2, "0")}
                                </div>
                            </div>
                            <div className="text-right">
                                <div className="text-[10px] uppercase tracking-wider text-amber-300/60">Duração</div>
                                <div className="font-mono text-amber-200 text-lg font-bold">{formatDuration(duration)}</div>
                            </div>
                        </div>

                        {conflicts.length > 0 && (
                            <div
                                data-testid="editor-conflicts"
                                className="mt-3 rounded-xl border border-red-500/40 bg-red-500/10 p-3 text-[12px] text-red-200"
                            >
                                <div className="flex items-center gap-2 font-bold uppercase tracking-wider text-[10px]">
                                    <i className="fa-solid fa-triangle-exclamation"></i>
                                    {conflicts.length} conflito(s) detectado(s)
                                </div>
                                <ul className="mt-1.5 space-y-0.5 text-[11px] opacity-90">
                                    {conflicts.slice(0, 3).map((c) => (
                                        <li key={c.id} className="font-mono">
                                            · {c.time} · {c.clientName} · {c.serviceName}
                                        </li>
                                    ))}
                                </ul>
                            </div>
                        )}
                    </section>

                    {/* Resumo */}
                    <section className="gold-panel p-5" data-testid="editor-summary">
                        <h3 className="gold-text text-sm uppercase tracking-wider pb-3 gold-border-bottom">
                            <i className="fa-solid fa-clipboard-list mr-2"></i>Resumo
                        </h3>
                        <dl className="mt-3 space-y-2 text-sm">
                            <SumRow label="Cliente" value={form.walkIn ? `${form.clientName || "—"} (avulso)` : selectedClient?.name || "—"} />
                            <SumRow label="Profissional" value={selectedPro?.name || "—"} />
                            <SumRow label="Serviço" value={selectedService?.name || "—"} />
                            <SumRow label="Início" value={`${form.timeStr}`} mono />
                            <SumRow label="Término" value={`${String(estimatedEnd.getHours()).padStart(2, "0")}:${String(estimatedEnd.getMinutes()).padStart(2, "0")}`} mono />
                            <SumRow label="Duração" value={formatDuration(duration)} mono />
                            <SumRow label="Valor" value={`R$ ${(selectedService?.price || 0).toFixed(2)}`} highlight />
                        </dl>
                        <label className="block mt-4">
                            <span className="text-[10px] uppercase tracking-wider text-amber-300/70 font-bold">Observações</span>
                            <textarea
                                data-testid="editor-notes"
                                value={form.notes}
                                onChange={(e) => setForm({ ...form, notes: e.target.value })}
                                rows={2}
                                placeholder="Observações internas (opcional)"
                                className="mt-1.5 w-full bg-black/60 border border-amber-500/25 rounded-xl px-3 py-2 text-amber-100 text-xs outline-none focus:border-amber-400/55 resize-none"
                            />
                        </label>
                    </section>

                    {/* Actions row for in-progress edits */}
                    {existing && existing.status === "agendado" && (
                        <button
                            data-testid="editor-quick-start"
                            onClick={() => { startAppointment(existing.id); toast.success("Serviço iniciado"); onClose(); }}
                            className="w-full min-h-[48px] rounded-xl border border-sky-500/40 bg-sky-500/15 hover:bg-sky-500/25 text-sky-100 text-[11px] uppercase tracking-wider font-bold"
                        >
                            <i className="fa-solid fa-play mr-2"></i>Iniciar Serviço Agora
                        </button>
                    )}
                    {existing && existing.status === "em-andamento" && (
                        <button
                            data-testid="editor-quick-finish"
                            onClick={() => { finishAppointment(existing.id); toast.success("Serviço concluído"); onClose(); }}
                            className="w-full min-h-[48px] rounded-xl border border-emerald-500/40 bg-emerald-500/15 hover:bg-emerald-500/25 text-emerald-100 text-[11px] uppercase tracking-wider font-bold"
                        >
                            <i className="fa-solid fa-check mr-2"></i>Concluir Serviço
                        </button>
                    )}

                    {/* Mobile-only Save */}
                    <div className="md:hidden flex flex-col gap-2">
                        <button
                            data-testid="agenda-editor-save-mobile"
                            onClick={handleSave}
                            className="gold-pill gold-pill-active min-h-[48px] rounded-xl text-[11px] font-bold uppercase tracking-[0.15em]"
                        >
                            Salvar Agendamento
                        </button>
                        {existing && (
                            <button
                                data-testid="agenda-editor-delete-mobile"
                                onClick={handleDelete}
                                className="min-h-[48px] rounded-xl border border-red-500/30 text-red-300 text-[11px] uppercase tracking-wider font-bold"
                            >
                                Excluir
                            </button>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
};

const SumRow = ({ label, value, mono, highlight }) => (
    <div className="flex items-center justify-between gap-2 py-1">
        <span className="text-[10px] uppercase tracking-wider text-amber-300/70 font-bold">{label}</span>
        <span
            className={`truncate ${mono ? "font-mono" : ""} ${highlight ? "text-emerald-300 font-bold" : "text-amber-100"}`}
        >
            {value}
        </span>
    </div>
);

export default Agenda;
